/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { IoCloseCircleSharp } from 'react-icons/io5'
import { FaFilter } from 'react-icons/fa'
import {
  FiSearch,
  FiMapPin,
  FiBarChart2,
  FiUsers,
  FiChevronLeft,
  FiChevronRight,
  FiX,
} from 'react-icons/fi'
import { parseSafeDate } from '../../constantes'
import ReactApexChart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import api from '../../services/axios'
import { Skeleton } from '../Skeletron'
import Filter from '../ClientDetailsModal/Filter'

interface Props {
  uf: string
  filters: any // Recebe os filtros da tela pai
  close: () => void
  convFamilia?: string
  fatorConv?: number
  isVolumeMode?: boolean
}

interface ClientData {
  cnpj_cpf_pdv: string
  nm_client: string
  uf?: string
  distribuidor_id?: string
  dist_id?: string
  city?: string
  cidade?: string
  municipio?: string
  bairro?: string
  address?: string
  endereco?: string
  cep?: string
}

interface ProductData {
  ean13?: string
  dun14?: string
  ean_display?: string
  ean?: string
  descricao: string
  marca?: string
  categoria?: string
  cod_cat?: string
  un_cx?: number | string
}

export const formatCaixa = (value: any) => {
  const n = Number(value)
  if (isNaN(n)) return value
  return `${n.toString().replace('.', ',')}`
}

const getSemanaReversa = (date: Date) => {
  const y = date.getFullYear()
  const m = date.getMonth()
  const ultimoDia = new Date(y, m + 1, 0).getDate()
  const dia = date.getDate()
  const diaS4Inicio = Math.max(1, ultimoDia - 6)
  const diaS3Inicio = Math.max(1, ultimoDia - 13)
  const diaS2Inicio = Math.max(1, ultimoDia - 20)
  if (dia >= diaS4Inicio) return 'S4'
  if (dia >= diaS3Inicio) return 'S3'
  if (dia >= diaS2Inicio) return 'S2'
  return 'S1'
}

export const MarketMarketDetailsModal: React.FC<Props> = ({
  uf,
  filters: initialFilters,
  close,
  convFamilia,
  fatorConv,
  isVolumeMode = false,
}) => {
  const [loading, setLoading] = useState(true)
  const [rawData, setRawData] = useState<any>(null)
  const [openFilter, setOpenFilter] = useState(false)
  const [activeTab, setActiveTab] = useState<'charts' | 'cities' | 'pdvs'>('charts')
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [citySearchQuery, setCitySearchQuery] = useState('')
  const [cityCurrentPage, setCityCurrentPage] = useState(1)
  const ITEMS_PER_PAGE = 20

  // Filtros internos do Modal
  const [modalFilters, setModalFilters] = useState({
    distribuidores: [] as string[],
    anos: [] as number[],
    meses: [] as number[],
    semanas: [] as string[],
    startDate: '',
    endDate: '',
    cidades: [] as string[],
    clientes: [] as string[],
    marcas: [] as string[],
    categorias: [] as string[],
  })

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const queryParams: any = { uf, ...initialFilters }

        if (queryParams.month !== undefined && queryParams.month !== null) {
          if (Number(queryParams.month) === -1) {
            queryParams.month = null
          } else {
            queryParams.month = Number(queryParams.month) + 1
          }
        }

        if (queryParams.endMonth !== undefined && queryParams.endMonth !== null) {
          if (Number(queryParams.endMonth) === -1) {
            queryParams.endMonth = null
          } else {
            queryParams.endMonth = Number(queryParams.endMonth) + 1
          }
        }

        const response = await api.get('/get/sellout/details', {
          params: queryParams,
        })
        setRawData(response.data)
      } catch (err) {
        console.error('Erro ao buscar detalhes:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [uf, initialFilters])

  // --- MEMOS DE MAPAS BÁSICOS ---
  const { produtosMap, distMap, clientsMap } = useMemo(() => {
    if (!rawData) return { produtosMap: new Map(), distMap: new Map(), clientsMap: new Map() }
    const { products, distribuidores, clients } = rawData

    const pMap = new Map<string, ProductData>()
    products?.forEach((p: ProductData) => {
      if (p.ean13) pMap.set(String(p.ean13).trim(), p)
      if (p.dun14) pMap.set(String(p.dun14).trim(), p)
      if (p.ean_display) {
        String(p.ean_display)
          .split(',')
          .forEach((code) => {
            const clean = code.trim()
            if (clean) pMap.set(clean, p)
          })
      }
      if (p.ean) pMap.set(String(p.ean).trim(), p)
    })

    const dMap = new Map<string, string>()
    distribuidores?.forEach((d: any) => dMap.set(String(d.id).trim(), d.name || `Dist. ${d.id}`))

    const cMap = new Map<string, ClientData>()
    clients?.forEach((c: ClientData) => cMap.set(String(c.cnpj_cpf_pdv).trim(), c))

    return { produtosMap: pMap, distMap: dMap, clientsMap: cMap }
  }, [rawData])

  // --- OPÇÕES DE FILTRO DINÂMICAS ---
  const filterOptions = useMemo(() => {
    if (!rawData) return null
    const dists = new Set<string>()
    const cidades = new Set<string>()
    const clis = new Map<string, string>()

    rawData.clients?.forEach((c: ClientData) => {
      const distId = String(c.distribuidor_id || c.dist_id || '').trim()
      const distName = distMap.get(distId)
      const city = c.city || c.cidade || c.municipio

      const atendeFiltroDist =
        modalFilters.distribuidores.length === 0 ||
        (distName && modalFilters.distribuidores.includes(distName))
      const atendeFiltroCidade =
        modalFilters.cidades.length === 0 || (city && modalFilters.cidades.includes(city))

      if (distName) dists.add(distName)
      if (atendeFiltroDist && city) cidades.add(city)
      if (atendeFiltroDist && atendeFiltroCidade && c.cnpj_cpf_pdv && c.nm_client) {
        clis.set(c.cnpj_cpf_pdv, c.nm_client)
      }
    })

    const marcas = new Set<string>()
    const categorias = new Set<string>()
    rawData.products?.forEach((p: ProductData) => {
      if (p.marca) marcas.add(p.marca)
      if (p.categoria) categorias.add(p.categoria)
    })

    return {
      distribuidores: Array.from(dists).map((d) => ({ label: d, value: d })),
      anos: [2022, 2023, 2024, 2025, 2026].map((a) => ({ label: String(a), value: a })),
      meses: Array.from({ length: 12 }).map((_, i) => ({
        label: String(i + 1).padStart(2, '0'),
        value: i + 1,
      })),
      semanas: [
        { label: 'Semana 1 (S1)', value: 'S1' },
        { label: 'Semana 2 (S2)', value: 'S2' },
        { label: 'Semana 3 (S3)', value: 'S3' },
        { label: 'Semana 4 (S4)', value: 'S4' },
      ],
      cidades: Array.from(cidades).map((c) => ({ label: c, value: c })),
      clientes: Array.from(clis.entries()).map(([cnpj, nome]) => ({ label: nome, value: cnpj })),
      marcas: Array.from(marcas).map((m) => ({ label: m, value: m })),
      categorias: Array.from(categorias).map((c) => ({ label: c, value: c })),
    }
  }, [rawData, distMap, modalFilters.distribuidores, modalFilters.cidades])

  // --- APLICAÇÃO DOS FILTROS (MASTER FILTER) ---
  const masterFilteredSellout = useMemo(() => {
    if (!rawData) return []
    return rawData.sellout.filter((item: any) => {
      const prodCode = String(item.produto).trim()
      const prod = produtosMap.get(prodCode)
      const cnpj = String(item.client).trim()
      const clientObj = clientsMap.get(cnpj)

      let itemDate: Date | null = null
      if (item.data) {
        itemDate = parseSafeDate(item.data)
      } else if (item.parsedDate) {
        itemDate = parseSafeDate(item.parsedDate)
      }

      if (modalFilters.startDate && modalFilters.endDate && itemDate) {
        const itemTime = itemDate.getTime()
        const start = parseSafeDate(modalFilters.startDate)?.setHours(0, 0, 0, 0) || 0
        const end = parseSafeDate(modalFilters.endDate)?.setHours(23, 59, 59, 999) || 0
        if (itemTime < start || itemTime > end) return false
      } else if (itemDate) {
        if (modalFilters.anos.length > 0 && !modalFilters.anos.includes(itemDate.getFullYear()))
          return false

        if (modalFilters.meses.length > 0) {
          const mesReal = itemDate.getMonth() + 1
          const mesesSelecionados = modalFilters.meses.map(Number)

          if (!mesesSelecionados.includes(mesReal)) {
            return false
          }
        }

        if (modalFilters.semanas && modalFilters.semanas.length > 0) {
          const itemSemana = getSemanaReversa(itemDate)
          if (!modalFilters.semanas.includes(itemSemana)) {
            return false
          }
        }
      }

      const temFiltroDist = modalFilters.distribuidores.length > 0
      const temFiltroCidade = modalFilters.cidades.length > 0

      if (temFiltroDist || temFiltroCidade) {
        if (!clientObj) return false
        const distId = String(clientObj.distribuidor_id || clientObj.dist_id || '').trim()
        const distName = distMap.get(distId)
        const city = clientObj.city || clientObj.cidade || clientObj.municipio

        if (temFiltroDist && (!distName || !modalFilters.distribuidores.includes(distName)))
          return false
        if (temFiltroCidade && (!city || !modalFilters.cidades.includes(city))) return false
      }

      if (modalFilters.clientes.length > 0 && !modalFilters.clientes.includes(cnpj)) return false

      if (modalFilters.marcas.length > 0 || modalFilters.categorias.length > 0) {
        if (!prod) return false
        if (
          modalFilters.marcas.length > 0 &&
          prod.marca &&
          !modalFilters.marcas.includes(prod.marca)
        )
          return false
        if (
          modalFilters.categorias.length > 0 &&
          prod.categoria &&
          !modalFilters.categorias.includes(prod.categoria) &&
          prod.cod_cat &&
          !modalFilters.categorias.includes(prod.cod_cat)
        ) {
          return false
        }
      }

      return true
    })
  }, [rawData, modalFilters, produtosMap, clientsMap, distMap])

  // --- DADOS DO GRÁFICO DE CLIENTES ---
  const clientChartData = useMemo(() => {
    const totals = new Map<string, number>()
    masterFilteredSellout.forEach((item: any) => {
      const cnpj = String(item.client).trim()
      const clientObj = clientsMap.get(cnpj)
      const clientName = clientObj?.nm_client || `CNPJ: ${cnpj}`

      const prod = produtosMap.get(String(item.produto).trim())
      if (!prod) return

      let calcValue = 0
      const qty = Number(item.qnt) || 0

      if (isVolumeMode) {
        const unCx = Number(prod.un_cx) || 1
        const fatorSeguro = Number(fatorConv) || 1
        calcValue = prod.cod_cat === convFamilia ? qty / fatorSeguro : qty / unCx
      } else {
        calcValue = Number(item.valor || 0) * qty
      }

      if (!isNaN(calcValue) && isFinite(calcValue)) {
        totals.set(clientName, (totals.get(clientName) || 0) + calcValue)
      }
    })

    const sorted = Array.from(totals.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)

    return {
      series: [
        {
          name: isVolumeMode ? 'Caixas' : 'Faturamento',
          data: sorted.map((e) => Number(e[1].toFixed(2))),
        },
      ],
      categories: sorted.map((e) => e[0]),
    }
  }, [masterFilteredSellout, produtosMap, clientsMap, isVolumeMode, fatorConv, convFamilia])

  // --- DADOS DO GRÁFICO DE PRODUTOS ---
  const itemChartData = useMemo(() => {
    const totals = new Map<string, number>()
    masterFilteredSellout.forEach((item: any) => {
      const prod = produtosMap.get(String(item.produto).trim())
      if (!prod) return

      let calcValue = 0
      const qty = Number(item.qnt) || 0

      if (isVolumeMode) {
        const unCx = Number(prod.un_cx) || 1
        const fatorSeguro = Number(fatorConv) || 1
        calcValue = prod.cod_cat === convFamilia ? qty / fatorSeguro : qty / unCx
      } else {
        calcValue = Number(item.valor || 0) * qty
      }

      if (!isNaN(calcValue) && isFinite(calcValue)) {
        const prodName = prod.descricao || 'Produto sem nome'
        totals.set(prodName, (totals.get(prodName) || 0) + calcValue)
      }
    })

    const sorted = Array.from(totals.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)

    return {
      series: [
        {
          name: isVolumeMode ? 'Caixas' : 'Faturamento',
          data: sorted.map((e) => Number(e[1].toFixed(2))),
        },
      ],
      categories: sorted.map((e) => e[0]),
    }
  }, [masterFilteredSellout, produtosMap, isVolumeMode, fatorConv, convFamilia])

  // --- LISTA DETALHADA DE PDVS (COM CIDADE, BAIRRO E ENDEREÇO) ---
  const pdvList = useMemo(() => {
    if (!rawData) return []
    const map = new Map<
      string,
      {
        cnpj: string
        name: string
        city: string
        bairro: string
        address: string
        cep: string
        distName: string
        totalValue: number
        totalVolume: number
        transactionsCount: number
      }
    >()

    masterFilteredSellout.forEach((item: any) => {
      const cnpj = String(item.client).trim()
      const clientObj = clientsMap.get(cnpj)
      const prod = produtosMap.get(String(item.produto).trim())
      const qty = Number(item.qnt) || 0

      let calcVal = 0
      if (isVolumeMode) {
        const unCx = Number(prod?.un_cx) || 1
        const fatorSeguro = Number(fatorConv) || 1
        calcVal = prod?.cod_cat === convFamilia ? qty / fatorSeguro : qty / unCx
      } else {
        calcVal = Number(item.valor || 0) * qty
      }

      if (!map.has(cnpj)) {
        const distId = String(
          clientObj?.distribuidor_id || clientObj?.dist_id || item.dist_id || '',
        ).trim()

        map.set(cnpj, {
          cnpj,
          name: clientObj?.nm_client || `PDV: ${cnpj}`,
          city: clientObj?.city || clientObj?.cidade || clientObj?.municipio || 'Não informada',
          bairro: clientObj?.bairro || 'Não informado',
          address: clientObj?.address || (clientObj as any)?.endereco || 'Não informado',
          cep: clientObj?.cep || '',
          distName: distMap.get(distId) || (distId ? `Dist. ${distId}` : 'Não informado'),
          totalValue: 0,
          totalVolume: 0,
          transactionsCount: 0,
        })
      }

      const pdv = map.get(cnpj)!
      pdv.totalValue += Number(item.valor || 0) * qty
      pdv.totalVolume += isVolumeMode ? calcVal : qty
      pdv.transactionsCount += 1
    })

    return Array.from(map.values()).sort((a, b) => {
      return isVolumeMode ? b.totalVolume - a.totalVolume : b.totalValue - a.totalValue
    })
  }, [masterFilteredSellout, clientsMap, produtosMap, distMap, isVolumeMode, fatorConv, convFamilia])

  // Filtro de busca de PDVs
  const filteredPdvs = useMemo(() => {
    if (!searchQuery.trim()) return pdvList
    const q = searchQuery.toLowerCase().trim()
    return pdvList.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.cnpj.toLowerCase().includes(q) ||
        p.city.toLowerCase().includes(q) ||
        p.bairro.toLowerCase().includes(q) ||
        p.address.toLowerCase().includes(q) ||
        p.distName.toLowerCase().includes(q),
    )
  }, [pdvList, searchQuery])

  // Totalizadores de PDVs
  const pdvStats = useMemo(() => {
    const citiesSet = new Set<string>()
    let totalVal = 0
    let totalVol = 0

    pdvList.forEach((p) => {
      if (p.city && p.city !== 'Não informada') citiesSet.add(p.city)
      totalVal += p.totalValue
      totalVol += p.totalVolume
    })

    return {
      totalPdvs: pdvList.length,
      totalCities: citiesSet.size,
      totalVal,
      totalVol,
    }
  }, [pdvList])

  // Paginação de PDVs
  const paginatedPdvs = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredPdvs.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredPdvs, currentPage])

  const totalPages = Math.max(1, Math.ceil(filteredPdvs.length / ITEMS_PER_PAGE))

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, activeTab])

  // --- LISTA DETALHADA DE CIDADES COM POTENCIAL E MÉTRICAS DE SKU ---
  const cityList = useMemo(() => {
    if (!rawData) return []

    // Agrupamento por Cidade
    const map = new Map<
      string,
      {
        cityName: string
        pdvsSet: Set<string>
        totalValue: number
        totalVolume: number
        transactionsCount: number
        citySkusSet: Set<string>
        pdvSkuCounts: Map<string, Set<string>>
      }
    >()

    masterFilteredSellout.forEach((item: any) => {
      const cnpj = String(item.client).trim()
      const clientObj = clientsMap.get(cnpj)
      const rawCity =
        clientObj?.city || clientObj?.cidade || clientObj?.municipio || 'Não informada'
      const cityClean = rawCity.trim() || 'Não informada'
      const normCity = cityClean.toUpperCase()

      const prodCode = String(item.produto).trim()
      const prod = produtosMap.get(prodCode)
      const qty = Number(item.qnt) || 0

      let calcVal = 0
      if (isVolumeMode) {
        const unCx = Number(prod?.un_cx) || 1
        const fatorSeguro = Number(fatorConv) || 1
        calcVal = prod?.cod_cat === convFamilia ? qty / fatorSeguro : qty / unCx
      } else {
        calcVal = Number(item.valor || 0) * qty
      }

      if (!map.has(normCity)) {
        map.set(normCity, {
          cityName: cityClean,
          pdvsSet: new Set<string>(),
          totalValue: 0,
          totalVolume: 0,
          transactionsCount: 0,
          citySkusSet: new Set<string>(),
          pdvSkuCounts: new Map<string, Set<string>>(),
        })
      }

      const cEntry = map.get(normCity)!
      cEntry.pdvsSet.add(cnpj)
      cEntry.totalValue += Number(item.valor || 0) * qty
      cEntry.totalVolume += isVolumeMode ? calcVal : qty
      cEntry.transactionsCount += 1
      if (prodCode) cEntry.citySkusSet.add(prodCode)

      if (!cEntry.pdvSkuCounts.has(cnpj)) {
        cEntry.pdvSkuCounts.set(cnpj, new Set<string>())
      }
      if (prodCode) {
        cEntry.pdvSkuCounts.get(cnpj)!.add(prodCode)
      }
    })

    const list = Array.from(map.values()).map((c) => {
      const pdvsCount = c.pdvsSet.size
      const totalSkusAcrossPdvs = Array.from(c.pdvSkuCounts.values()).reduce(
        (acc, s) => acc + s.size,
        0,
      )
      const avgSkusPerPdv = pdvsCount > 0 ? totalSkusAcrossPdvs / pdvsCount : 0
      const avgTicket =
        pdvsCount > 0
          ? isVolumeMode
            ? c.totalVolume / pdvsCount
            : c.totalValue / pdvsCount
          : 0

      let potentialStatus: 'opportunity' | 'expanding' | 'consolidated' = 'consolidated'
      let potentialLabel = 'Consolidada'
      if (pdvsCount <= 2) {
        potentialStatus = 'opportunity'
        potentialLabel = 'Alta Oportunidade'
      } else if (avgSkusPerPdv < 5) {
        potentialStatus = 'expanding'
        potentialLabel = 'Expansão de Mix'
      }

      return {
        cityName: c.cityName,
        pdvsCount,
        totalValue: c.totalValue,
        totalVolume: c.totalVolume,
        transactionsCount: c.transactionsCount,
        uniqueSkus: c.citySkusSet.size,
        avgSkusPerPdv,
        avgTicket,
        potentialStatus,
        potentialLabel,
      }
    })

    return list.sort((a, b) =>
      isVolumeMode ? b.totalVolume - a.totalVolume : b.totalValue - a.totalValue,
    )
  }, [masterFilteredSellout, clientsMap, produtosMap, isVolumeMode, fatorConv, convFamilia])

  // Filtro de busca de Cidades
  const filteredCityList = useMemo(() => {
    if (!citySearchQuery.trim()) return cityList
    const q = citySearchQuery.toLowerCase().trim()
    return cityList.filter((c) => c.cityName.toLowerCase().includes(q))
  }, [cityList, citySearchQuery])

  // Paginação de Cidades
  const paginatedCities = useMemo(() => {
    const start = (cityCurrentPage - 1) * ITEMS_PER_PAGE
    return filteredCityList.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredCityList, cityCurrentPage])

  const totalCityPages = Math.max(1, Math.ceil(filteredCityList.length / ITEMS_PER_PAGE))

  useEffect(() => {
    setCityCurrentPage(1)
  }, [citySearchQuery, activeTab])

  // --- OPÇÕES DOS GRÁFICOS (DESIGN) ---
  const optionsProducts: ApexOptions = useMemo(
    () => ({
      chart: { type: 'bar', toolbar: { show: false } },
      xaxis: { categories: itemChartData.categories, labels: { style: { colors: '#ffffff' } } },
      yaxis: { labels: { style: { colors: '#ffffff' } } },
      colors: ['#a6c10b'],
      plotOptions: { bar: { horizontal: false, columnWidth: '45%', borderRadius: 4 } },
      dataLabels: { enabled: false },
      tooltip: {
        y: {
          formatter: (val) =>
            isVolumeMode
              ? `${val.toLocaleString('pt-BR')} CX`
              : `R$ ${val.toLocaleString('pt-BR')}`,
        },
      },
    }),
    [itemChartData.categories, isVolumeMode],
  )

  const optionsClients: ApexOptions = useMemo(
    () => ({
      chart: { type: 'bar', toolbar: { show: false } },
      xaxis: { categories: clientChartData.categories, labels: { style: { colors: '#ffffff' } } },
      yaxis: { labels: { style: { colors: '#ffffff', fontSize: '10px' }, maxWidth: 150 } },
      colors: ['#007aff'],
      plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
      dataLabels: { enabled: false },
      tooltip: {
        y: {
          formatter: (val) =>
            isVolumeMode
              ? `${val.toLocaleString('pt-BR')} CX`
              : `R$ ${val.toLocaleString('pt-BR')}`,
        },
      },
    }),
    [clientChartData.categories, isVolumeMode],
  )

  return (
    <Overlay>
      <Container>
        <Main>
          <Header>
            <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
              <button
                type='button'
                style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                onClick={() => setOpenFilter(!openFilter)}
                title='Filtros'
              >
                <FaFilter style={{ color: '#ffffff', fontSize: 22 }} />
              </button>
              <h2>
                Análise Detalhada: {uf}
                {initialFilters?.semana && initialFilters.semana !== 'todas'
                  ? ` (${initialFilters.semana})`
                  : modalFilters.semanas.length > 0
                  ? ` (${modalFilters.semanas.join(', ')})`
                  : ''}
              </h2>
            </div>

            {/* Alternador de Abas */}
            <TabGroup>
              <TabButton
                $active={activeTab === 'charts'}
                onClick={() => setActiveTab('charts')}
                type='button'
              >
                <FiBarChart2 size={14} />
                Visão Gráfica
              </TabButton>
              <TabButton
                $active={activeTab === 'cities'}
                onClick={() => setActiveTab('cities')}
                type='button'
              >
                <FiMapPin size={14} />
                Cidades & Potencial ({cityList.length})
              </TabButton>
              <TabButton
                $active={activeTab === 'pdvs'}
                onClick={() => setActiveTab('pdvs')}
                type='button'
              >
                <FiUsers size={14} />
                Lista de PDVs ({pdvList.length})
              </TabButton>
            </TabGroup>

            <CloseBtn onClick={close} title='Fechar'>
              <IoCloseCircleSharp />
            </CloseBtn>
          </Header>

          {loading ? (
            <div
              style={{
                display: 'flex',
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Skeleton />
            </div>
          ) : activeTab === 'charts' ? (
            <ChartsGrid>
              <ChartBox>
                <h4>Top 10 Produtos ({isVolumeMode ? 'CX' : 'R$'})</h4>
                {itemChartData.categories.length > 0 ? (
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <ReactApexChart
                      options={optionsProducts}
                      series={itemChartData.series}
                      type='bar'
                      height='100%'
                    />
                  </div>
                ) : (
                  <EmptyState>Nenhuma venda identificada.</EmptyState>
                )}
              </ChartBox>

              <ChartBox>
                <h4>Comparativo de Clientes ({isVolumeMode ? 'CX' : 'R$'})</h4>
                {clientChartData.categories.length > 0 ? (
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <ReactApexChart
                      options={optionsClients}
                      series={clientChartData.series}
                      type='bar'
                      height='100%'
                    />
                  </div>
                ) : (
                  <EmptyState>Nenhum cliente identificado.</EmptyState>
                )}
              </ChartBox>
            </ChartsGrid>
          ) : activeTab === 'cities' ? (
            <PdvsContainer>
              {/* Barra Superior com Busca e KPIs de Cidades */}
              <PdvsToolBar>
                <SearchInputWrapper>
                  <FiSearch color='#94a3b8' size={16} />
                  <input
                    type='text'
                    placeholder='Buscar Cidade pelo Nome...'
                    value={citySearchQuery}
                    onChange={(e) => setCitySearchQuery(e.target.value)}
                  />
                  {citySearchQuery && (
                    <button
                      type='button'
                      onClick={() => setCitySearchQuery('')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#94a3b8',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      title='Limpar busca'
                    >
                      <FiX size={14} />
                    </button>
                  )}
                </SearchInputWrapper>

                <PdvKpiRow>
                  <PdvKpiCard>
                    <span className='label'>Cidades Atendidas</span>
                    <strong className='val'>{cityList.length}</strong>
                  </PdvKpiCard>
                  <PdvKpiCard>
                    <span className='label'>Média PDVs / Cidade</span>
                    <strong className='val'>
                      {cityList.length > 0 ? (pdvStats.totalPdvs / cityList.length).toFixed(1) : '0'}
                    </strong>
                  </PdvKpiCard>
                  <PdvKpiCard>
                    <span className='label'>Ticket Médio Geral</span>
                    <strong className='val highlight'>
                      {isVolumeMode
                        ? `${(pdvStats.totalPdvs > 0 ? pdvStats.totalVol / pdvStats.totalPdvs : 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} CX`
                        : new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          }).format(pdvStats.totalPdvs > 0 ? pdvStats.totalVal / pdvStats.totalPdvs : 0)}
                    </strong>
                  </PdvKpiCard>
                </PdvKpiRow>
              </PdvsToolBar>

              {/* Tabela de Cidades & Potencial */}
              <TableWrapper>
                {paginatedCities.length > 0 ? (
                  <StyledTable>
                    <thead>
                      <tr>
                        <th>Cidade / Município</th>
                        <th style={{ textAlign: 'center' }}>PDVs Atendidos</th>
                        <th style={{ textAlign: 'right' }}>
                          {isVolumeMode ? 'Volume Total (CX)' : 'Faturamento Total (R$)'}
                        </th>
                        <th style={{ textAlign: 'right' }}>Ticket Médio / PDV</th>
                        <th style={{ textAlign: 'center' }}>Mix Médio (SKUs/PDV)</th>
                        <th>Diagnóstico de Potencial</th>
                        <th style={{ textAlign: 'center' }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedCities.map((city) => (
                        <tr key={city.cityName}>
                          <td>
                            <div className='client-cell'>
                              <span className='name' title={city.cityName}>
                                {city.cityName}
                              </span>
                              <span className='cnpj'>{uf} • {city.uniqueSkus} SKUs Únicos</span>
                            </div>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span className='geo-badge'>
                              <span className='uf-tag'>{city.pdvsCount} PDVs</span>
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <span className='value-tag'>
                              {isVolumeMode
                                ? `${city.totalVolume.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} CX`
                                : new Intl.NumberFormat('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                  }).format(city.totalValue)}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <span style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 12 }}>
                              {isVolumeMode
                                ? `${city.avgTicket.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} CX`
                                : new Intl.NumberFormat('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                  }).format(city.avgTicket)}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span
                              style={{
                                color: city.avgSkusPerPdv >= 5 ? '#4ade80' : '#fbbf24',
                                fontWeight: 700,
                                fontSize: 12,
                              }}
                            >
                              {city.avgSkusPerPdv.toFixed(1)} SKUs
                            </span>
                          </td>
                          <td>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '3px 8px',
                                borderRadius: 6,
                                fontSize: 11,
                                fontWeight: 600,
                                background:
                                  city.potentialStatus === 'opportunity'
                                    ? 'rgba(56, 189, 248, 0.15)'
                                    : city.potentialStatus === 'expanding'
                                    ? 'rgba(234, 179, 8, 0.15)'
                                    : 'rgba(34, 197, 94, 0.15)',
                                color:
                                  city.potentialStatus === 'opportunity'
                                    ? '#38bdf8'
                                    : city.potentialStatus === 'expanding'
                                    ? '#eab308'
                                    : '#22c55e',
                                border: `1px solid ${
                                  city.potentialStatus === 'opportunity'
                                    ? 'rgba(56, 189, 248, 0.3)'
                                    : city.potentialStatus === 'expanding'
                                    ? 'rgba(234, 179, 8, 0.3)'
                                    : 'rgba(34, 197, 94, 0.3)'
                                }`,
                              }}
                            >
                              {city.potentialLabel}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              type='button'
                              onClick={() => {
                                setSearchQuery(city.cityName)
                                setActiveTab('pdvs')
                              }}
                              style={{
                                background: 'rgba(166, 193, 11, 0.15)',
                                border: '1px solid rgba(166, 193, 11, 0.35)',
                                color: '#a6c10b',
                                padding: '4px 10px',
                                borderRadius: 6,
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                              }}
                              title={`Ver ${city.pdvsCount} PDVs de ${city.cityName}`}
                            >
                              Ver PDVs ({city.pdvsCount})
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </StyledTable>
                ) : (
                  <EmptyState>
                    {citySearchQuery
                      ? `Nenhuma cidade encontrada para a busca "${citySearchQuery}".`
                      : 'Nenhuma cidade identificada para este estado.'}
                  </EmptyState>
                )}
              </TableWrapper>

              {/* Paginação de Cidades */}
              {totalCityPages > 1 && (
                <PaginationBar>
                  <span className='page-info'>
                    Mostrando {(cityCurrentPage - 1) * ITEMS_PER_PAGE + 1} a{' '}
                    {Math.min(cityCurrentPage * ITEMS_PER_PAGE, filteredCityList.length)} de{' '}
                    {filteredCityList.length} Cidades
                  </span>

                  <div className='btn-group'>
                    <PaginationBtn
                      type='button'
                      disabled={cityCurrentPage <= 1}
                      onClick={() => setCityCurrentPage((p) => Math.max(1, p - 1))}
                    >
                      <FiChevronLeft size={16} /> Anterior
                    </PaginationBtn>
                    <span className='page-badge'>
                      {cityCurrentPage} / {totalCityPages}
                    </span>
                    <PaginationBtn
                      type='button'
                      disabled={cityCurrentPage >= totalCityPages}
                      onClick={() => setCityCurrentPage((p) => Math.min(totalCityPages, p + 1))}
                    >
                      Próximo <FiChevronRight size={16} />
                    </PaginationBtn>
                  </div>
                </PaginationBar>
              )}
            </PdvsContainer>
          ) : (
            <PdvsContainer>
              {/* Barra Superior com Busca e KPIs de PDVs */}
              <PdvsToolBar>
                <SearchInputWrapper>
                  <FiSearch color='#94a3b8' size={16} />
                  <input
                    type='text'
                    placeholder='Buscar por Nome, CNPJ, Cidade ou Bairro...'
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      type='button'
                      onClick={() => setSearchQuery('')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#94a3b8',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      title='Limpar busca'
                    >
                      <FiX size={14} />
                    </button>
                  )}
                </SearchInputWrapper>

                <PdvKpiRow>
                  <PdvKpiCard>
                    <span className='label'>Total de PDVs</span>
                    <strong className='val'>{pdvStats.totalPdvs.toLocaleString('pt-BR')}</strong>
                  </PdvKpiCard>
                  <PdvKpiCard>
                    <span className='label'>Cidades Atendidas</span>
                    <strong className='val'>{pdvStats.totalCities}</strong>
                  </PdvKpiCard>
                  <PdvKpiCard>
                    <span className='label'>
                      {isVolumeMode ? 'Volume Total (CX)' : 'Faturamento Total (R$)'}
                    </span>
                    <strong className='val highlight'>
                      {isVolumeMode
                        ? `${pdvStats.totalVol.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} CX`
                        : new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          }).format(pdvStats.totalVal)}
                    </strong>
                  </PdvKpiCard>
                </PdvKpiRow>
              </PdvsToolBar>

              {/* Tabela de PDVs */}
              <TableWrapper>
                {paginatedPdvs.length > 0 ? (
                  <StyledTable>
                    <thead>
                      <tr>
                        <th>PDV / Razão Social</th>
                        <th>Cidade / UF</th>
                        <th>Bairro / Endereço</th>
                        <th>Distribuidor</th>
                        <th style={{ textAlign: 'right' }}>
                          {isVolumeMode ? 'Volume (CX)' : 'Total (R$)'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPdvs.map((pdv) => (
                        <tr key={pdv.cnpj}>
                          <td>
                            <div className='client-cell'>
                              <span className='name' title={pdv.name}>
                                {pdv.name}
                              </span>
                              <span className='cnpj'>CNPJ: {pdv.cnpj}</span>
                            </div>
                          </td>
                          <td>
                            <div className='geo-badge'>
                              <span className='city'>{pdv.city}</span>
                              <span className='uf-tag'>{uf}</span>
                            </div>
                          </td>
                          <td>
                            <div className='address-cell'>
                              <span className='bairro'>
                                {pdv.bairro !== 'Não informado' ? `Bairro ${pdv.bairro}` : '—'}
                              </span>
                              {pdv.address !== 'Não informado' && (
                                <span className='address' title={pdv.address}>
                                  {pdv.address}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className='dist-tag'>{pdv.distName}</span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <span className='value-tag'>
                              {isVolumeMode
                                ? `${pdv.totalVolume.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} CX`
                                : new Intl.NumberFormat('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                  }).format(pdv.totalValue)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </StyledTable>
                ) : (
                  <EmptyState>
                    {searchQuery
                      ? `Nenhum PDV encontrado para a busca "${searchQuery}".`
                      : 'Nenhum PDV identificado para este estado.'}
                  </EmptyState>
                )}
              </TableWrapper>

              {/* Paginação */}
              {totalPages > 1 && (
                <PaginationBar>
                  <span className='page-info'>
                    Mostrando {(currentPage - 1) * ITEMS_PER_PAGE + 1} a{' '}
                    {Math.min(currentPage * ITEMS_PER_PAGE, filteredPdvs.length)} de{' '}
                    {filteredPdvs.length} PDVs
                  </span>

                  <div className='btn-group'>
                    <PaginationBtn
                      type='button'
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    >
                      <FiChevronLeft size={16} /> Anterior
                    </PaginationBtn>
                    <span className='page-badge'>
                      {currentPage} / {totalPages}
                    </span>
                    <PaginationBtn
                      type='button'
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Próximo <FiChevronRight size={16} />
                    </PaginationBtn>
                  </div>
                </PaginationBar>
              )}
            </PdvsContainer>
          )}
        </Main>

        {openFilter && filterOptions && (
          <Filter
            closeFilter={() => setOpenFilter(false)}
            options={filterOptions}
            filters={modalFilters}
            setFilters={setModalFilters}
          />
        )}
      </Container>
    </Overlay>
  )
}

// --- STYLED COMPONENTS ---
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  z-index: 9999999999999999;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Container = styled.div`
  position: relative;
  width: 95%;
  height: 90%;
  background: rgb(0, 24, 66);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.8);
  display: flex;
  overflow: hidden;
`

const Main = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 0;
`

const Header = styled.div`
  padding: 16px 24px;
  background: #001842;
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  h2 {
    font-size: 18px;
    font-weight: 700;
  }
`

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
    color: #ff4d4f;
  }
`

const TabGroup = styled.div`
  display: flex;
  gap: 6px;
  background: rgba(0, 0, 0, 0.3);
  padding: 4px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.06);
`

const TabButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${(props) => (props.$active ? '#a6c10b' : 'transparent')};
  color: ${(props) => (props.$active ? '#001842' : '#94a3b8')};
  font-weight: ${(props) => (props.$active ? '700' : '500')};
  font-size: 12px;
  border: none;
  border-radius: 7px;
  padding: 6px 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${(props) => (props.$active ? '#001842' : '#ffffff')};
  }
`

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  flex: 1;
  padding: 20px;
  min-height: 0;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }
`

const ChartBox = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  height: 100%;
  h4 {
    color: #ffffff;
    margin-bottom: 15px;
    font-size: 15px;
  }
`

const EmptyState = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 13px;
  padding: 40px;
`

// --- ELEMENTOS DE PDVS ---
const PdvsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 20px;
  min-height: 0;
  overflow: hidden;
`

const PdvsToolBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`

const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 8px 14px;
  width: 380px;
  max-width: 100%;

  input {
    background: transparent;
    border: none;
    outline: none;
    color: #ffffff;
    font-size: 13px;
    width: 100%;

    &::placeholder {
      color: #64748b;
    }
  }
`

const PdvKpiRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const PdvKpiCard = styled.div`
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 10px;
  padding: 6px 14px;

  .label {
    font-size: 10px;
    color: #94a3b8;
    text-transform: uppercase;
  }

  .val {
    font-size: 14px;
    font-weight: 700;
    color: #ffffff;

    &.highlight {
      color: #a6c10b;
    }
  }
`

const TableWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.06);

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 3px;
  }
`

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 12px;

  thead {
    position: sticky;
    top: 0;
    background: #001233;
    z-index: 5;

    th {
      padding: 12px 16px;
      color: #94a3b8;
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid rgba(255, 255, 255, 0.04);
      transition: background 0.15s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.03);
      }

      td {
        padding: 12px 16px;
        color: #e2e8f0;
        vertical-align: middle;
      }
    }
  }

  .client-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .name {
      font-weight: 600;
      color: #ffffff;
      font-size: 12px;
      max-width: 260px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .cnpj {
      font-size: 10px;
      color: #64748b;
    }
  }

  .geo-badge {
    display: flex;
    align-items: center;
    gap: 6px;

    .city {
      font-weight: 500;
      color: #38bdf8;
    }

    .uf-tag {
      font-size: 9px;
      background: rgba(56, 189, 248, 0.15);
      color: #38bdf8;
      padding: 2px 5px;
      border-radius: 4px;
      font-weight: 700;
    }
  }

  .address-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
    max-width: 250px;

    .bairro {
      font-size: 11px;
      color: #cbd5e1;
      font-weight: 500;
    }

    .address {
      font-size: 10px;
      color: #64748b;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .dist-tag {
    font-size: 11px;
    color: #cbd5e1;
    background: rgba(255, 255, 255, 0.04);
    padding: 3px 8px;
    border-radius: 6px;
    display: inline-block;
  }

  .value-tag {
    font-weight: 700;
    color: #a6c10b;
    font-size: 13px;
  }
`

const PaginationBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 14px;
  flex-wrap: wrap;
  gap: 10px;

  .page-info {
    font-size: 12px;
    color: #94a3b8;
  }

  .btn-group {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .page-badge {
    font-size: 12px;
    font-weight: 600;
    color: #ffffff;
    padding: 0 6px;
  }
`

const PaginationBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #ffffff;
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #a6c10b;
    color: #001842;
    border-color: #a6c10b;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`
